import type { BlockDef } from "../modules/types";
import { getTheme } from "../themes";
import { installedModules } from "@/modules/registry.generated";
import { RichTextBlock } from "./RichTextBlock";
import { FaqBlock } from "./FaqBlock";
import { EmbedBlock } from "./EmbedBlock";
import { CookiePreferencesBlock, CookieTableBlock, PrivacyPolicyBlock } from "./PrivacyBlocks";
import { SAMPLE_PRIVACY_POLICY } from "./privacy-sample";

const coreBlocks: BlockDef[] = [{ type: "cms:rich-text", label: "Tekst", icon: "article", component: RichTextBlock,
  defaults: () => ({ heading: {}, body: {} }) },
{ type: "cms:faq", label: "Veelgestelde vragen", icon: "quiz", component: FaqBlock,
  defaults: () => ({ heading: { nl: "Veelgestelde vragen", en: "Frequently asked questions", de: "Häufige Fragen" }, intro: {}, items: [{ question: {}, answer: {} }] }) },
{ type: "cms:embed", label: "Video of kaart", icon: "smart_display", component: EmbedBlock,
  defaults: () => ({ heading: {}, url: {}, caption: {} }) },
{ type: "cms:privacy-policy", label: "Privacyverklaring (automatisch)", icon: "policy", component: PrivacyPolicyBlock,
  sample: { data: SAMPLE_PRIVACY_POLICY },
  defaults: () => ({ heading: { nl: "Privacy- en cookieverklaring", en: "Privacy and cookie statement", de: "Datenschutz- und Cookie-Erklärung" } }) },
{ type: "cms:cookie-table", label: "Cookie-overzicht", icon: "cookie", component: CookieTableBlock, sample: { data: SAMPLE_PRIVACY_POLICY }, defaults: () => ({}) },
{ type: "cms:cookie-preferences", label: "Cookievoorkeuren", icon: "toggle_on", component: CookiePreferencesBlock, defaults: () => ({}) }];

const blockOwners = new Map<string, { module: typeof installedModules[number]; def: BlockDef }>();
const attributeOwners = new Map<string, { module: typeof installedModules[number]; component: NonNullable<typeof installedModules[number]["editorAttributes"]>[string] }>();
for (const module of installedModules) {
  for (const def of module.blocks || []) {
    if (blockOwners.has(def.type)) throw new Error(`[module:${module.id}] duplicate module block type ${def.type}`);
    blockOwners.set(def.type, { module, def });
  }
  for (const [type, component] of Object.entries(module.editorAttributes || {})) {
    if (attributeOwners.has(type)) throw new Error(`[module:${module.id}] duplicate editor attribute type ${type}`);
    attributeOwners.set(type, { module, component });
  }
}

function active(module: typeof installedModules[number], enabled: Set<string>): boolean {
  return enabled.has(module.id) && Object.keys(module.dependencies).every((dependency) => enabled.has(dependency));
}

/** All block types available for a theme + the enabled modules. */
export function getBlockDefs(themeId: string | undefined, enabledModules: string[]): BlockDef[] {
  const defs: BlockDef[] = [...coreBlocks, ...getTheme(themeId).blocks];
  const enabled = new Set(enabledModules);
  for (const module of installedModules) {
    if (!enabled.has(module.id) || !module.capabilities.includes("blocks")) continue;
    if (!Object.keys(module.dependencies).every((dependency) => enabled.has(dependency))) continue;
    defs.push(...(module.blocks || []));
  }
  return defs;
}

export function getBlockDef(type: string, themeId: string | undefined, enabledModules: string[]): BlockDef | undefined {
  const core = coreBlocks.find((def) => def.type === type);
  if (core) return core;
  const themeDef = getTheme(themeId).blocks.find((def) => def.type === type);
  if (themeDef) return themeDef;
  const owned = blockOwners.get(type);
  if (!owned || !owned.module.capabilities.includes("blocks")) return undefined;
  return active(owned.module, new Set(enabledModules)) ? owned.def : undefined;
}

export function getModuleEditorAttribute(type: string, enabledModules: string[]) {
  const enabled = new Set(enabledModules);
  const owned = attributeOwners.get(type);
  if (!owned || !owned.module.capabilities.includes("blocks") || !active(owned.module, enabled)) return undefined;
  return owned.component;
}
